// src/components/GlobalComponents/CustomPaginator.tsx
import React, { useEffect, useState } from 'react';
import { ChevronsLeft, ChevronLeft, ChevronRight, ChevronsRight, List } from 'lucide-react';
import { C, G } from './uiTokens';

interface CustomPaginatorProps {
  page: number;
  totalPages: number;
  totalRecords: number;
  pageSize: number;
  onPageChange: (newPage: number) => void;
  onPageSizeChange?: (newSize: number) => void; // optional: shows the rows-per-page select
  pageSizeOptions?: number[];                   // optional, defaults below
  showInfoBar?: boolean;
}

const DEFAULT_PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

const CustomPaginator: React.FC<CustomPaginatorProps> = ({
  page,
  totalPages,
  totalRecords,
  pageSize,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS,
  showInfoBar = true,
}) => {
  // "Go to" keeps its own draft value and commits on Enter / blur
  const [goto, setGoto] = useState(String(page));
  useEffect(() => { setGoto(String(page)); }, [page]);

  if (totalPages <= 1 && !onPageSizeChange) return null;

  const startRecord = totalRecords === 0 ? 0 : (page - 1) * pageSize + 1;
  const endRecord = Math.min(page * pageSize, totalRecords);

  const generatePageNumbers = () => {
    const pages: (number | string)[] = [];
    let start = Math.max(1, page - 2);
    let end = Math.min(totalPages, page + 2);

    if (page <= 3) end = Math.min(totalPages, 5);
    if (page >= totalPages - 2) start = Math.max(1, totalPages - 4);

    if (start > 1) {
      pages.push(1);
      if (start > 2) pages.push('...');
    }
    for (let i = start; i <= end; i++) pages.push(i);
    if (end < totalPages) {
      if (end < totalPages - 1) pages.push('...');
      pages.push(totalPages);
    }
    return pages;
  };

  const commitGoto = () => {
    const val = parseInt(goto, 10);
    if (!Number.isNaN(val) && val >= 1 && val <= totalPages && val !== page) onPageChange(val);
    else setGoto(String(page));
  };

  const atFirst = page === 1;
  const atLast = page === totalPages;

  return (
    <>
      <style>{`
        .cp-nav-btn, .cp-page-btn {
          display: inline-flex; align-items: center; justify-content: center;
          height: 27.7px; border-radius: 6.5px; padding: 0;
          border: 1px solid ${G.line}; background: white; color: ${C.teal};
          font-size: 0.74rem; font-weight: 600; font-family: inherit; cursor: pointer;
          transition: background .15s ease, border-color .15s ease, color .15s ease, transform .1s ease, box-shadow .15s ease;
        }
        .cp-nav-btn { width: 27.7px; }
        .cp-page-btn { min-width: 27.7px; padding: 0 6.5px; }
        .cp-nav-btn:hover:not(:disabled),
        .cp-page-btn:hover:not(.active) { background: rgba(17,85,80,0.07); border-color: rgba(17,85,80,0.2); }
        .cp-nav-btn:active:not(:disabled), .cp-page-btn:active { transform: scale(0.94); }
        .cp-nav-btn:disabled { opacity: 0.4; cursor: not-allowed; }
        .cp-page-btn.active {
          background: linear-gradient(135deg, ${C.teal}, ${C.tealMid});
          color: ${C.lime}; border-color: transparent; font-weight: 700;
          box-shadow: 0 2px 7px rgba(17,85,80,0.3); cursor: default;
        }
        .cp-ellipsis {
          display: inline-flex; align-items: center; justify-content: center;
          min-width: 20px; height: 27.7px; color: ${C.muted}; font-weight: 600; font-size: 0.74rem;
        }

        .cp-size-select {
          height: 27.7px; padding: 0 24px 0 9px; border-radius: 6.5px;
          border: 1px solid ${G.line}; background-color: white;
          font-size: 0.69rem; font-weight: 600; font-family: inherit; color: ${C.teal};
          cursor: pointer; outline: none; appearance: none; -webkit-appearance: none;
          background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6'%3E%3Cpath d='M1 1l4 4 4-4' stroke='%23115550' stroke-opacity='0.5' stroke-width='1.5' fill='none' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");
          background-repeat: no-repeat; background-position: right 8px center;
          transition: border-color .15s ease, box-shadow .15s ease;
        }
        .cp-size-select:hover { border-color: rgba(17,85,80,0.3); }
        .cp-size-select:focus { border-color: rgba(17,85,80,0.5); box-shadow: 0 0 0 3px rgba(17,85,80,0.08); }

        .cp-goto-input {
          width: 46px; height: 27.7px; box-sizing: border-box; padding: 0 6px;
          border-radius: 6.5px; border: 1px solid ${G.line};
          font-size: 0.69rem; font-weight: 600; font-family: inherit; color: ${C.teal};
          text-align: center; outline: none;
          transition: border-color .15s ease, box-shadow .15s ease;
        }
        .cp-goto-input:hover { border-color: rgba(17,85,80,0.3); }
        .cp-goto-input:focus { border-color: rgba(17,85,80,0.5); box-shadow: 0 0 0 3px rgba(17,85,80,0.08); }
        .cp-goto-input::-webkit-outer-spin-button,
        .cp-goto-input::-webkit-inner-spin-button { -webkit-appearance: none; margin: 0; }
        .cp-goto-input[type=number] { -moz-appearance: textfield; }
      `}</style>

      {/* ── Info bar ─────────────────────────────────────────────── */}
      {showInfoBar && (
        <div style={{
          padding: '10.1px 16.6px', borderTop: `1px solid ${G.soft}`, background: '#fafbfb',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '9.2px',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '7.4px' }}>
            <div style={{
              width: '25px', height: '25px', borderRadius: '6.5px', flexShrink: 0,
              background: `linear-gradient(135deg, ${C.teal}, ${C.tealMid})`, color: C.lime,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <List size={12} />
            </div>
            <span style={{ fontSize: '0.7rem', color: C.sub, fontWeight: 600 }}>
              Showing {startRecord}–{endRecord} of {totalRecords} entries
            </span>
          </div>
          <span style={{
            padding: '2.8px 9.2px', borderRadius: '20px', fontSize: '0.63rem', fontWeight: 700,
            background: 'rgba(17,85,80,0.07)', color: C.tealMid, border: '1px solid rgba(17,85,80,0.18)',
          }}>
            Page {page} of {totalPages}
          </span>
        </div>
      )}

      {/* ── Controls ─────────────────────────────────────────────── */}
      <div style={{
        background: 'white',
        borderTop: `1px solid ${G.soft}`,
        padding: '11px 16.6px',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '11px',
      }}>
        {/* Left: per-page selector, or a static label */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '7.4px' }}>
          {onPageSizeChange ? (
            <>
              <span style={{ fontSize: '0.69rem', color: C.muted, fontWeight: 600 }}>Rows per page</span>
              <select
                className="cp-size-select"
                value={pageSize}
                onChange={e => {
                  onPageSizeChange(Number(e.target.value));
                  onPageChange(1); // reset to page 1 on size change
                }}
              >
                {pageSizeOptions.map(opt => <option key={opt} value={opt}>{opt}</option>)}
              </select>
            </>
          ) : (
            <span style={{ fontSize: '0.69rem', color: C.muted, fontWeight: 500 }}>{pageSize} per page</span>
          )}
        </div>

        {/* Center: page buttons */}
        {totalPages > 1 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '3.7px' }}>
            <button className="cp-nav-btn" onClick={() => onPageChange(1)} disabled={atFirst} aria-label="First page" title="First page">
              <ChevronsLeft size={13} />
            </button>
            <button className="cp-nav-btn" onClick={() => onPageChange(page - 1)} disabled={atFirst} aria-label="Previous page" title="Previous page">
              <ChevronLeft size={13} />
            </button>

            <div style={{ display: 'flex', alignItems: 'center', gap: '3.7px', margin: '0 3.7px' }}>
              {generatePageNumbers().map((p, idx) =>
                typeof p === 'string' ? (
                  <span key={`ellipsis-${idx}`} className="cp-ellipsis">···</span>
                ) : (
                  <button
                    key={p}
                    className={`cp-page-btn ${p === page ? 'active' : ''}`}
                    onClick={() => p !== page && onPageChange(p as number)}
                    aria-label={`Page ${p}`}
                    aria-current={p === page ? 'page' : undefined}
                  >
                    {p}
                  </button>
                )
              )}
            </div>

            <button className="cp-nav-btn" onClick={() => onPageChange(page + 1)} disabled={atLast} aria-label="Next page" title="Next page">
              <ChevronRight size={13} />
            </button>
            <button className="cp-nav-btn" onClick={() => onPageChange(totalPages)} disabled={atLast} aria-label="Last page" title="Last page">
              <ChevronsRight size={13} />
            </button>
          </div>
        )}

        {/* Right: go-to input */}
        {totalPages > 1 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '7.4px' }}>
            <span style={{ fontSize: '0.69rem', color: C.muted, fontWeight: 600 }}>Go to</span>
            <input
              className="cp-goto-input"
              type="number"
              min={1}
              max={totalPages}
              value={goto}
              onChange={e => setGoto(e.target.value)}
              onBlur={commitGoto}
              onKeyDown={e => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
              aria-label="Go to page"
            />
          </div>
        )}
      </div>
    </>
  );
};

export default CustomPaginator;
